import { ArrowLeft, Mic, FlaskConical, BarChart3, Workflow, FileSearch, Cpu, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'

const modelsTested = [
  {
    name: 'CosyVoice 3 Instruct',
    type: 'Instruction-following TTS',
    description: 'Advanced speech synthesis model supporting instruction-based generation for voice cloning, emotion control, and multilingual output. Primary focus for robustness testing.',
    strengths: 'High naturalness, instruction following, multi-language',
    weaknesses: 'Computational overhead, edge-case instruction parsing',
  },
  {
    name: 'Qwen3 TTS',
    type: 'Neural TTS',
    description: 'Text-to-speech model from the Qwen family, optimized for natural prosody and voice quality. Evaluated for production deployment readiness.',
    strengths: 'Natural prosody, fast inference, clean voice quality',
    weaknesses: 'Limited instruction control, single language focus',
  },
  {
    name: 'Qwen3 Omni',
    type: 'Multimodal',
    description: 'Multimodal model combining text understanding with speech generation. Tested for cross-modal consistency and instruction following in complex scenarios.',
    strengths: 'Multimodal reasoning, contextual speech generation',
    weaknesses: 'Higher latency, resource-intensive inference',
  },
  {
    name: 'ParlerTTS',
    type: 'Controllable TTS',
    description: 'Open-weight TTS model with fine-grained control over speaking style, pacing, and emphasis. Evaluated for prompt-based voice customization.',
    strengths: 'Style control, open weights, customizable',
    weaknesses: 'Lower naturalness than proprietary models',
  },
  {
    name: 'NISQA',
    type: 'Speech Quality Assessment',
    description: 'Non-intrusive speech quality assessment model used as the evaluation metric. Provides MOS (Mean Opinion Score) predictions for generated audio quality measurement.',
    strengths: 'Objective quality scoring, no reference audio needed',
    weaknesses: 'May not capture all perceptual quality dimensions',
  },
]

const methodologySteps = [
  {
    step: '01',
    title: 'Test Scenario Design',
    description: 'Designed comprehensive test scenarios covering production-like error conditions: malformed inputs, edge-case text, special characters, extremely long sequences, and mixed-language content.',
  },
  {
    step: '02',
    title: 'Automated Pipeline Construction',
    description: 'Built automated testing pipelines that systematically feed test inputs to each model, capture outputs, handle failures gracefully, and log detailed performance metrics.',
  },
  {
    step: '03',
    title: 'Quality Assessment',
    description: 'Evaluated all generated audio using NISQA for objective quality scoring, supplemented by manual listening tests for subjective assessment of naturalness and intelligibility.',
  },
  {
    step: '04',
    title: 'Error Pattern Analysis',
    description: 'Analyzed failure modes across models, categorizing errors by type (audio artifacts, dropped content, pronunciation errors, timing issues) and frequency.',
  },
  {
    step: '05',
    title: 'Robustness Benchmarking',
    description: 'Created comparative robustness scores for each model across all error scenarios, identifying strengths, weaknesses, and optimal use cases for production deployment.',
  },
]

export default function AudioSynthesisPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                M.Sc. Research Project · Universitat Stuttgart
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                Robustness Testing of <br />
                <span className="text-primary">Speech Synthesis Models</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                Master's research on systematic robustness evaluation of state-of-the-art TTS models under production-like error scenarios with automated testing pipelines.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">5</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Models Evaluated</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">NISQA</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Quality Metric</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">Auto</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Test Pipelines</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Research Overview */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Research Overview</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  This research addresses a critical gap in speech synthesis evaluation: most TTS models are tested only on clean, well-formed text, but production systems encounter noisy, malformed, and edge-case inputs constantly.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <FlaskConical className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Research Question</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    How do modern speech synthesis models behave under production-like error conditions, and which models demonstrate the highest robustness for real-world deployment?
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                  <h3 className="text-2xl font-bold text-white mb-6 font-headline">Research Objectives</h3>
                  <div className="space-y-4">
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 font-bold text-sm">1</div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Systematic Robustness Evaluation</h4>
                        <p className="text-sm text-on-surface-variant">Design and execute comprehensive test scenarios covering all major categories of production input errors.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 font-bold text-sm">2</div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Automated Testing Infrastructure</h4>
                        <p className="text-sm text-on-surface-variant">Build reproducible pipelines for batch testing, failure capture, and metric collection across multiple models.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center text-secondary flex-shrink-0 font-bold text-sm">3</div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Comparative Analysis</h4>
                        <p className="text-sm text-on-surface-variant">Produce actionable robustness benchmarks to guide model selection for production speech synthesis systems.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 font-bold text-sm">4</div>
                      <div>
                        <h4 className="font-bold text-white mb-1">Error Pattern Taxonomy</h4>
                        <p className="text-sm text-on-surface-variant">Develop a classification system for TTS failure modes to enable systematic model improvement tracking.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Models Tested */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Models Evaluated</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Five models spanning instruction-based TTS, neural TTS, multimodal generation, and quality assessment were systematically tested.
              </p>
            </div>
            <div className="space-y-6">
              {modelsTested.map((model, index) => (
                <div key={index} className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <Mic size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white font-headline">{model.name}</h3>
                        <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">{model.type}</span>
                      </div>
                      <p className="text-on-surface-variant mb-4 leading-relaxed">{model.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-surface rounded-lg">
                          <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Strengths</h4>
                          <p className="text-sm text-on-surface-variant">{model.strengths}</p>
                        </div>
                        <div className="p-4 bg-surface rounded-lg">
                          <h4 className="text-xs font-bold text-red-400 uppercase tracking-widest mb-1">Weaknesses</h4>
                          <p className="text-sm text-on-surface-variant">{model.weaknesses}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Methodology</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Five-phase research methodology from test design through robustness benchmarking, ensuring comprehensive and reproducible evaluation.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {methodologySteps.map((step, index) => (
                <div key={index} className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-7xl font-bold text-primary/5 font-headline">{step.step}</div>
                  <div className="relative z-10">
                    <h3 className="text-lg font-bold text-white mb-3 font-headline">{step.title}</h3>
                    <p className="text-sm text-on-surface-variant leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pipeline Architecture */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Pipeline Architecture</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Automated testing pipeline architecture for systematic robustness evaluation of speech synthesis models.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Workflow className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Test Input Generator</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed mb-4">
                  Generates diverse test inputs including: normal text (baseline), special characters and symbols, extremely long sequences (&gt;500 chars), mixed-language content, malformed phoneme sequences, and numbers/dates/currencies in various formats.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Python</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Faker</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Custom Generators</span>
                </div>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Cpu className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Model Inference Engine</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed mb-4">
                  Orchestrates model inference with timeout handling, error capture, and retry logic. Supports batch processing with configurable concurrency and resource-aware scheduling to prevent GPU memory exhaustion.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">PyTorch</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Async IO</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">CUDA</span>
                </div>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <BarChart3 className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Quality Assessment</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed mb-4">
                  Runs all generated audio through NISQA for MOS prediction, supplemented by custom audio analysis (silence detection, clipping, sample rate validation). Results stored in structured format for statistical analysis.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">NISQA</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Librosa</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">NumPy</span>
                </div>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <FileSearch className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Results Aggregator</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed mb-4">
                  Aggregates per-test results into model-level robustness profiles with statistical summaries, error rate calculations, failure mode categorization, and comparative rankings across all evaluated models.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Pandas</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">Matplotlib</span>
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">JSON</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Achievements */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-white font-headline text-center">Technical Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-primary text-center">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                  <Shield size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 font-headline">Error Scenario Coverage</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Designed 50+ test scenarios across 8 error categories, achieving comprehensive coverage of production-like failure modes rarely tested in academic settings.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-primary text-center">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                  <Workflow size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 font-headline">Pipeline Automation</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Built fully automated testing infrastructure capable of running end-to-end evaluations across all 5 models with minimal human intervention and comprehensive logging.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-secondary text-center">
                <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary mx-auto mb-4">
                  <BarChart3 size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 font-headline">Reproducible Research</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  All test scenarios, evaluation scripts, and results are structured for reproducibility, enabling the research community to extend and validate findings.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Research?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full research methodology, testing pipeline code, and evaluation results are available for academic review and collaboration.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-primary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(164,230,255,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
